import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SearchIcon from '@mui/icons-material/Search';
import PageContainer from '../../../components/Redesing/page-container';
import CustomToast from '../../../components/Redesing/custom-toast';
import {
  archiveChatTag,
  archiveChatTagCategory,
  restoreChatTagCategory,
  updateChatTag,
  updateChatTagCategory,
} from '../../../hooks/chat/api';
import { paths } from '../../../paths';
import { ChatTag, ChatTagCategory, useChatTagGlossary } from '../tagGlossary';
import { CategoryDialog } from './CategoryDialog';
import { RestoreTagDialog } from './RestoreTagDialog';
import { TagDialog } from './TagDialog';
import { TagRow, TagTable } from './TagTable';
import {
  searchGlossary,
  swapWrites,
  visibleCategories,
  visibleTags,
} from './glossaryPage';

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

type TagDialogState = { category: ChatTagCategory; tag: ChatTag | null } | null;

/**
 * CS's chat tag glossary: the rider-support categories in sheet order and,
 * for the selected one, its tags with everything CS wrote. Everyone with
 * support access reads it; ant's `canEdit` (in phase 1, a Movatic admin)
 * shows the edit controls. Every write re-reads the glossary, since
 * glossary edits publish no subscription.
 */
export default function SupportTagsPage() {
  const { glossary, canEdit, loading, error, refetch } = useChatTagGlossary();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const [query, setQuery] = useState('');
  const [tagDialog, setTagDialog] = useState<TagDialogState>(null);
  const [categoryDialog, setCategoryDialog] = useState<{
    category: ChatTagCategory | null;
  } | null>(null);
  const [restoring, setRestoring] = useState<ChatTag | null>(null);
  /** The row (tag or category) whose Retire was clicked once. */
  const [confirmRetireId, setConfirmRetireId] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);

  useEffect(() => {
    document.title = 'Chat tags';
  }, []);

  const categories = useMemo(
    () => (glossary ? visibleCategories(glossary, showRetired) : []),
    [glossary, showRetired]
  );

  // The first listed category opens by itself; a selection that the
  // Show retired switch hides falls back to it.
  const selected = useMemo(
    () => categories.find((c) => c.id === selectedId) ?? categories[0] ?? null,
    [categories, selectedId]
  );

  const searching = query.trim() !== '';
  const rows = useMemo<TagRow[]>(() => {
    if (!glossary) return [];
    if (searching) return searchGlossary(glossary, query, showRetired);
    if (!selected) return [];
    return visibleTags(selected, showRetired).map((tag) => ({ tag, category: selected }));
  }, [glossary, searching, query, showRetired, selected]);

  const saidError = (failure: string) => (e: unknown) =>
    CustomToast({ type: 'error', message: `${failure} ${errorText(e)}`, duration: 6000 });

  /** One write, then the glossary is re-read; a refusal is said. */
  const write = async (
    work: () => Promise<unknown>,
    success: string,
    failure: string
  ) => {
    setWriting(true);
    try {
      await work();
      CustomToast({ type: 'success', message: success });
      await refetch();
    } catch (e) {
      saidError(failure)(e);
    } finally {
      setWriting(false);
      setConfirmRetireId(null);
    }
  };

  const retireTag = (row: TagRow) => {
    if (confirmRetireId !== row.tag.id) {
      setConfirmRetireId(row.tag.id);
      return;
    }
    write(
      () => archiveChatTag(row.tag.id),
      `"${row.tag.label}" retired`,
      'Could not retire the tag.'
    );
  };

  const retireCategory = (category: ChatTagCategory) => {
    if (confirmRetireId !== category.id) {
      setConfirmRetireId(category.id);
      return;
    }
    write(
      () => archiveChatTagCategory(category.id),
      `"${category.name}" retired`,
      'Could not retire the category.'
    );
  };

  const restoreCategory = (category: ChatTagCategory) =>
    write(
      () => restoreChatTagCategory(category.id),
      `"${category.name}" restored`,
      'Could not restore the category.'
    );

  const moveTag = (row: TagRow, index: number, direction: 'up' | 'down') => {
    const writes = swapWrites(
      rows.map((r) => ({ id: r.tag.id, position: r.tag.position })),
      index,
      direction
    );
    if (!writes) return;
    write(
      async () => {
        await updateChatTag(writes[0].id, { position: writes[0].position });
        await updateChatTag(writes[1].id, { position: writes[1].position });
      },
      `"${row.tag.label}" moved`,
      'Could not move the tag.'
    );
  };

  const moveCategory = (category: ChatTagCategory, direction: 'up' | 'down') => {
    const index = categories.findIndex((c) => c.id === category.id);
    const writes = swapWrites(
      categories.map((c) => ({ id: c.id, position: c.position })),
      index,
      direction
    );
    if (!writes) return;
    write(
      async () => {
        await updateChatTagCategory(writes[0].id, { position: writes[0].position });
        await updateChatTagCategory(writes[1].id, { position: writes[1].position });
      },
      `"${category.name}" moved`,
      'Could not move the category.'
    );
  };

  const onSaved = (what: 'added' | 'saved', noun: string) => {
    CustomToast({ type: 'success', message: `${noun} ${what}` });
    refetch();
  };

  const selectedIndex = selected ? categories.findIndex((c) => c.id === selected.id) : -1;

  return (
    <PageContainer spaceName="Chat tags">
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          gap={2}
        >
          <Button
            component={RouterLink}
            to={paths.support}
            startIcon={<ArrowBackIcon />}
            sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
          >
            Support Chat
          </Button>
          <Box sx={{ flex: 1 }} />
          <TextField
            size="small"
            placeholder="Search tags"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputProps={{ 'aria-label': 'Search tags' }}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showRetired}
                onChange={(e) => setShowRetired(e.target.checked)}
                inputProps={{ 'aria-label': 'Show retired' }}
              />
            }
            label="Show retired"
          />
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={writing}
              onClick={() => setCategoryDialog({ category: null })}
            >
              Add category
            </Button>
          )}
        </Stack>

        {error && <Alert severity="error">Could not load the glossary. {error}</Alert>}
        {glossary && !canEdit && (
          <Typography variant="caption" color="text.secondary" data-testid="read-only">
            Read-only: for now only Movatic admins edit the glossary.
          </Typography>
        )}

        {loading && !glossary && (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        )}

        {glossary && (
          <Card variant="outlined" sx={{ display: 'flex', minHeight: 480 }}>
            <Box
              sx={{
                width: { xs: 220, md: 300 },
                flexShrink: 0,
                borderRight: 1,
                borderColor: 'divider',
                overflowY: 'auto',
              }}
            >
              <List dense disablePadding data-testid="category-list">
                {categories.map((category) => (
                  <ListItemButton
                    key={category.id}
                    selected={!searching && selected?.id === category.id}
                    onClick={() => {
                      setSelectedId(category.id);
                      setQuery('');
                    }}
                    divider
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {category.name}
                          </Typography>
                          {category.kind === 'marker' && (
                            <Chip size="small" variant="outlined" label="Marker" />
                          )}
                          {category.archivedAt && (
                            <Chip size="small" variant="outlined" label="Retired" />
                          )}
                        </Stack>
                      }
                      secondary={`${visibleTags(category, showRetired).length} tags`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
              {searching ? (
                <Typography variant="subtitle1" sx={{ p: 2 }}>
                  {rows.length} tag{rows.length === 1 ? '' : 's'} matching “{query.trim()}
                  ”
                </Typography>
              ) : (
                selected && (
                  <Box sx={{ p: 2 }} data-testid="category-detail">
                    <Stack direction="row" alignItems="flex-start" gap={2}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography variant="h6">{selected.name}</Typography>
                          <Chip
                            size="small"
                            label={selected.archivedAt ? 'Retired' : 'Active'}
                            color={selected.archivedAt ? 'default' : 'success'}
                            variant={selected.archivedAt ? 'outlined' : 'filled'}
                          />
                        </Stack>
                        {selected.description && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {selected.description}
                          </Typography>
                        )}
                        {selected.notes && (
                          <Typography variant="caption" color="text.secondary">
                            {selected.notes}
                          </Typography>
                        )}
                      </Box>
                      {canEdit && (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <Tooltip title="Move category up">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Move category up"
                                disabled={writing || selectedIndex <= 0}
                                onClick={() => moveCategory(selected, 'up')}
                              >
                                <ArrowUpwardIcon fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move category down">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Move category down"
                                disabled={
                                  writing || selectedIndex >= categories.length - 1
                                }
                                onClick={() => moveCategory(selected, 'down')}
                              >
                                <ArrowDownwardIcon fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Button
                            size="small"
                            disabled={writing}
                            onClick={() => setCategoryDialog({ category: selected })}
                          >
                            Edit category
                          </Button>
                          {selected.archivedAt ? (
                            <Button
                              size="small"
                              disabled={writing}
                              onClick={() => restoreCategory(selected)}
                            >
                              Restore category
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              color="error"
                              disabled={writing}
                              onClick={() => retireCategory(selected)}
                              onBlur={() =>
                                setConfirmRetireId((id) =>
                                  id === selected.id ? null : id
                                )
                              }
                            >
                              {confirmRetireId === selected.id
                                ? 'Confirm retire'
                                : 'Retire category'}
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddIcon />}
                            disabled={writing}
                            onClick={() =>
                              setTagDialog({ category: selected, tag: null })
                            }
                          >
                            Add tag
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                )
              )}
              <Divider />
              <TagTable
                rows={rows}
                showCategory={searching}
                canEdit={canEdit}
                confirmRetireId={confirmRetireId}
                onEdit={(row) => setTagDialog({ category: row.category, tag: row.tag })}
                onRetire={retireTag}
                onRestore={(row) => setRestoring(row.tag)}
                onMove={moveTag}
                onRetireBlur={() => setConfirmRetireId(null)}
              />
            </Box>
          </Card>
        )}
      </Stack>

      {tagDialog && (
        <TagDialog
          open
          category={tagDialog.category}
          tag={tagDialog.tag}
          onClose={() => setTagDialog(null)}
          onSaved={(what) => onSaved(what, 'Tag')}
          onError={saidError('Could not save the tag.')}
        />
      )}
      {categoryDialog && (
        <CategoryDialog
          open
          category={categoryDialog.category}
          onClose={() => setCategoryDialog(null)}
          onSaved={(what) => onSaved(what, 'Category')}
          onError={saidError('Could not save the category.')}
        />
      )}
      <RestoreTagDialog
        open={restoring !== null}
        tag={restoring}
        onClose={() => setRestoring(null)}
        onRestored={() => {
          CustomToast({ type: 'success', message: 'Tag restored' });
          refetch();
        }}
        onError={saidError('Could not restore the tag.')}
      />
    </PageContainer>
  );
}
